/**
 * SlimHeader — Morphing header for Portfolio page.
 *
 * Normal mode: "Portfolio" title + search icon.
 * Select mode: 22px select-all circle + count + "selected" + Cancel + search.
 * Select + search: Pill-shaped search input takes over entire header.
 *
 * Spec compliance:
 * - NO × close button (Cancel is a text button on the right)
 * - NO bulk action icons in header (those live in MorphingBottomBar)
 * - NO square checkbox or "All" text label
 * - Count: fontWeight 500, color #1A1A1A (NOT bold, NOT teal)
 * - Select-all: 22px circle, empty when 0, minus when partial, check when all
 */

import { useRef, useEffect } from 'react';
import { T } from '@/styles/tokens';
import { SearchButton } from './SearchButton';

interface SlimHeaderProps {
  score: number | null;
  selMode: boolean;
  selCount: number;
  totalFiltered: number;
  onSelectAll: () => void;
  onExitSel: () => void;
  onSearchOpen: () => void;
  searchOpen: boolean;
  /** Select-mode inline search query (only used when selMode && searchOpen) */
  selSearchQuery?: string;
  onSelSearchQuery?: (q: string) => void;
}

const HEADER_H = 64;

export function SlimHeader({
  selMode, selCount, totalFiltered,
  onSelectAll, onExitSel,
  onSearchOpen, searchOpen,
  selSearchQuery = '', onSelSearchQuery,
}: SlimHeaderProps) {
  const allSelected = selCount === totalFiltered && selCount > 0;
  const pillInputRef = useRef<HTMLInputElement>(null);
  const isSelSearch = selMode && searchOpen;

  // Auto-focus pill input when select search opens
  useEffect(() => {
    if (isSelSearch) {
      setTimeout(() => pillInputRef.current?.focus(), 80);
    }
  }, [isSelSearch]);

  return (
    <div style={{
      position: selMode ? 'fixed' : 'sticky',
      top: 0,
      left: selMode ? 0 : undefined,
      right: selMode ? 0 : undefined,
      maxWidth: selMode ? 420 : undefined,
      margin: selMode ? '0 auto' : undefined,
      zIndex: isSelSearch ? 40 : (searchOpen ? 52 : 40),
      background: isSelSearch ? 'rgba(250,248,243,0.92)' : (searchOpen ? 'transparent' : 'rgba(250,248,243,0.92)'),
      backdropFilter: isSelSearch ? 'blur(16px)' : (searchOpen ? 'none' : 'blur(16px)'),
      WebkitBackdropFilter: isSelSearch ? 'blur(16px)' : (searchOpen ? 'none' : 'blur(16px)'),
      borderBottom: selMode ? `1px solid ${T.border}` : 'none',
      pointerEvents: (!selMode && searchOpen) ? 'none' : 'auto',
      height: HEADER_H,
      display: 'flex',
      alignItems: 'center',
      padding: '0 16px 0 20px',
    }}>

      {/* ── Select-mode pill search (takes over entire header) ── */}
      {isSelSearch && (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', gap: 8,
          marginRight: 8,
        }}>
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 8,
            background: T.borderLight,
            borderRadius: 20, padding: '0 14px', height: 40,
          }}>
            {/* Magnifying glass */}
            <svg width="15" height="15" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
              <circle cx="8.5" cy="8.5" r="6" stroke="#A3A09A" strokeWidth="1.8" />
              <path d="M13 13l4.5 4.5" stroke="#A3A09A" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <input
              ref={pillInputRef}
              type="search"
              placeholder="Filter pieces\u2026"
              value={selSearchQuery}
              onChange={e => onSelSearchQuery?.(e.target.value)}
              style={{
                flex: 1, fontSize: 15, background: 'transparent',
                border: 'none', outline: 'none', color: T.text,
                fontFamily: T.fontFamily,
                padding: '8px 0', caretColor: T.primary,
                letterSpacing: '-0.01em',
              }}
            />
            {selSearchQuery && (
              <button
                onClick={() => onSelSearchQuery?.('')}
                style={{
                  width: 22, height: 22, borderRadius: 11,
                  background: 'rgba(26,26,26,0.06)', border: 'none',
                  cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, padding: 0,
                }}
              >
                <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                  <path d="M2 2l6 6M8 2l-6 6" stroke={T.textSecondary} strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Normal header content (hidden during select search) ── */}
      {!isSelSearch && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {/* "Portfolio" text — centered, morphs out on select */}
          <h1 style={{
            fontSize: 24, fontWeight: 800, color: T.text, margin: 0,
            letterSpacing: '-0.02em',
            fontFamily: "'Manrope','Satoshi','DM Sans',sans-serif",
            WebkitFontSmoothing: 'antialiased' as const,
            position: 'absolute',
            left: 0,
            right: 0,
            textAlign: 'center',
            opacity: selMode ? 0 : (searchOpen ? 0 : 1),
            transform: selMode ? 'translateX(-20px) scale(0.8)' : 'translateX(0) scale(1)',
            transition: 'all 250ms cubic-bezier(0.25, 1, 0.5, 1)',
            pointerEvents: selMode ? 'none' : 'auto',
          }}>
            Portfolio
          </h1>

          {/* Select circle + count — morphs in */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            opacity: selMode ? 1 : 0,
            transform: selMode ? 'translateX(0) scale(1)' : 'translateX(-12px) scale(0.85)',
            transition: 'all 280ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            pointerEvents: selMode ? 'auto' : 'none',
          }}>
            {/* Select-all circle — 22px, empty when 0, minus when partial, check when all */}
            <button onClick={onSelectAll} style={{
              width: 22, height: 22, borderRadius: 11, border: 'none',
              background: allSelected ? T.primary : 'transparent',
              boxShadow: `0 0 0 1.5px ${allSelected ? T.primary : selCount > 0 ? T.textMuted : '#D4D1CB'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              flexShrink: 0, padding: 0,
            }}>
              {allSelected ? (
                <svg width="11" height="11" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <polyline points="20,6 9,17 4,12" />
                </svg>
              ) : selCount > 0 ? (
                <svg width="9" height="9" fill="none" stroke="#A3A09A" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24">
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              ) : null}
            </button>

            {/* Count + "selected" */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <span style={{
                fontSize: 18, fontWeight: 500, color: T.text,
                letterSpacing: '-0.01em',
                fontFamily: T.fontFamily,
              }}>
                {selCount}
              </span>
              <span style={{
                fontSize: 13, fontWeight: 400, color: T.textSecondary,
                fontFamily: T.fontFamily,
              }}>
                selected
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Right side: Cancel (select mode, not searching) + search toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        {selMode && !searchOpen && (
          <button onClick={onExitSel} style={{
            background: 'none', border: 'none', color: T.textSecondary,
            fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '6px 8px',
            fontFamily: T.fontFamily,
            transition: 'opacity 200ms ease 100ms',
          }}>
            Cancel
          </button>
        )}
        <div style={{ pointerEvents: 'auto' }}>
          <SearchButton isOpen={searchOpen} onClick={onSearchOpen} />
        </div>
      </div>
    </div>
  );
}
